/**
 * @file cssHelpers.js
 * @fileOverview Dynamically manages CSS variables
 * @author Smartmatic
 * @creationDate 2019-11-07
 * @modificationDate 2025-03-14
 */
import cssVars from 'css-vars-ponyfill';
import throttle from 'lodash/throttle';

const contestListWrapperPosition = 'calc(24px + (100vw - 1200px) / 2)';
const contentWrapperMargin = 'calc(48px + 346px + 24px)';

let cssVariablesState = {
  '--hundred-vh': `${window.innerHeight}px`,
  '--ninety-vh': `${window.innerHeight * 0.9}px`,
  '--seventy-vh': `${window.innerHeight * 0.7}px`,
  '--white': '#FFFFFF',
  '--black': '#000000',
  '--lblue': '#00B4E9',
  '--blue': '#20499F',
  '--blue-text': '#20499F',
  '--red': '#F03A47',
  '--yellow': '#FFCB1A',
  '--lgray': '#F5F5F5',
  '--mgray': '#d8d8d8',
  '--mmgray': '#D9D9D9',
  '--xmgray': '#B4B4B4',
  '--gray': '#979797',
  '--dgray': '#4A4A4A',
  '--review-gray': '#D8D8D8',
  '--font-size-base': '10px',
  '--font-size-base-12': '1.2rem',
  '--font-size-base-14': '1.4rem',
  '--font-size-base-16': '1.6rem',
  '--font-size-base-18': '1.8rem',
  '--font-size-base-20': '2rem',
  '--font-size-base-22': '2.2rem',
  '--font-size-base-24': '2.4rem',
  '--font-size-base-28': '2.8rem',
  '--font-size-base-31': '3.1rem',
  '--font-size-base-32': '3.2rem',
  '--font-size-base-34': '3.4rem',
  '--font-size-base-36': '3.6rem',
  '--font-size-base-40': '4rem',
  '--font-size-base-48': '4.8rem',
  '--font-a': '1.4rem',
  '--font-small': '1.4rem',
  '--font-small-10': '1.6rem',
  '--font-small-20': '1.8rem',
  '--font-medium': '2rem',
  '--font-medium-10': '2.4rem',
  '--font-medium-15': '2.7rem',
  '--font-medium-20': '2.8rem',
  '--font-medium-30': '3.6rem',
  '--font-large': '3.2rem',
  '--font-large-20': '3.8rem',
  '--font-xlarge': '4rem',
  '--font-xxlarge': '4.8rem',
  '--header-height': '64px',
  '--header-height-large': '85px',
  '--header-modal-height': '100px',
  '--footer-height': '64px',
  '--footer-height-large': '85px',
  '--max-width': '1063px',
  '--med-btn-width': '180px',
  '--lg-btn-width': '243px',
  '--xlg-btn-width': '347px',
  '--wayfinding-step-height': '187px',
  '--privacy-choice-height': '420px',
  '--candidate-height': '80px',
  '--sample-text-height': '130px',
  '--review-modal-max-width': '540px',
  '--chevron-invert': '100%',
  '--icon-invert': '0%',
  '--max-content-width': '1200px',
  '--title-copy-margin': '20px',
  '--title-copy-margin-m': '20px',
  '--title-copy-margin-l': '20px',
  '--body-copy-margin': '20px',
  '--body-copy-margin-m': '20px',
  '--body-copy-margin-l': '20px',
  '--header-spacer-12-4': '12px',
  /* for Farsi and Korean language support */
  /* scaleX -1 vs 1 - mirrors/flips everything.
  We use this on containers for flipped layout and then "flip back" content inside of it
  Sometimes a few "flips" are needed.
  */
  '--scale-x': 'scaleX(1)',
  '--text-direction': 'ltr',
  '--text-align': 'left',
  '--word-break': 'normal',

  /* 320px Special Attention */
  '--iphone5-header-default-button-width': '68px',
  '--iphone5-header-default-button-right': '14px',
  '--iphone5-header-review-button-width': '74px',
  '--iphone5-header-review-button-right': '4px',
  '--iphone5-header-crest-left': '0px',

  /** Selections page positioning (rtl support) - see note below in setLanguageCSS */
  '--contest-list-wrapper-right': 'initial',
  '--contest-list-wrapper-left': contestListWrapperPosition,
  '--content-wrapper-margin-right': 'initial',
  '--content-wrapper-margin-left': contentWrapperMargin,
  '--text-direction-reverse': 'rtl',
};

/**
 * Applies hover styles to the body element of a document if the device does not
 * support touch events.
 *
 * This function detects whether the current device supports touch events by checking
 * if the events 'ontouchstart' exist in the document body or if the navigator object
 * contains a property 'maxTouchPoints' or 'msMaxTouchPoints' that is greater than 0.
 * If the device does not support touch events, it adds the class 'allowHover' to the
 * body element of the document. This function is useful for scenarios where hover
 * interactions need to be managed based on the device type.
 */
function setHoverStyles() {
  if (
    !(
      'ontouchstart' in document.documentElement ||
      navigator.maxTouchPoints > 0 ||
      navigator.msMaxTouchPoints > 0
    )
  ) {
    document.body.classList.add('allowHover');
  }
}

/**
 * Sets and updates CSS variables for viewport heights (vh) in percentages on window resize.
 *
 * Fixes known issue in some browsers (e.g., Chrome on Galaxy S9) that don't calculate vh
 * correctly. Instead of using native vh, it calculates it and assigns it to CSS variables.
 * Variables for 100, 90, and 70 vh are created. When resizing, it triggers a throttle function
 * which checks if the window height has changed. If it has, the CSS variables are updated with
 * the new dimensions. Note that this function doesn't return any explicit value as it manipulates
 * global state and intends to affect the document's styling state.
 */
function setVH() {
  let prevWindowHeight = window.innerHeight;

  /*
  Some browsers don't calculate vh correctly which led to issues - for example, footer on Galaxy S9 on Chrome on ballot and poll pass pages
  So instead of using vh, calculating it and assigning it to a variable https://css-tricks.com/the-trick-to-viewport-units-on-mobile/
  For now easier to have these 3 variables for 100, 90, and 70. If we use vh in many more ways may be easier to just calc 1vh unit as --vh
  And use it ase e.g. calc(var(--vh) * 90)
  */
  window.addEventListener(
    'resize',
    throttle(() => {
      if (prevWindowHeight !== window.innerHeight) {
        prevWindowHeight = window.innerHeight;
        const variables = {
          '--hundred-vh': `${window.innerHeight}px`,
          '--ninety-vh': `${window.innerHeight * 0.9}px`,
          '--seventy-vh': `${window.innerHeight * 0.7}px`,
        };

        cssVariablesState = { ...cssVariablesState, ...variables };

        cssVars({
          preserveVars: false,
          variables: cssVariablesState,
        });
      }
    }, 200)
  );
}

/**
 * Initializes CSS variables compatible with all browsers, including Internet Explorer 11.
 *
 * This function performs a two-fold action. First, it sets CSS variables using the values in
 * cssVariablesState object. Then, it configures the cssVars to 'watch' CSS variables, thereby
 * allowing any modifications to them to take immediate effect. It does not preserve original
 * variables after the re-declaration, ensuring that the same set of variables is used
 * throughout the life-cycle, promoting cleaner and more readable code.
 */
function initializeCSSVariables() {
  /* equivalent of document.documentElement.style.setProperty('--var', 'value');
         but also works for IE 11 */
  /* Initialize with initial variables. */
  cssVars({
    preserveVars: false,
    variables: cssVariablesState,
  });
  /* Then watch recycling variables already there rather than continuing to use initial variables. */

  cssVars({
    watch: true,
    preserveVars: false,
  });
}

/**
 * Initializes CSS styles for the application.
 *
 * This function runs various setup functions to initialize hover styles, viewport
 * height (VH), and CSS variables. It is typically run once, at application startup.
 *
 * At the initial load of the application, it sets the custom styles for hover effects,
 * dynamically adjusts the height of the viewport to align with browser window size,
 * and initializes the CSS variables.
 */
export function initializeCSS() {
  setHoverStyles();
  setVH();
  initializeCSSVariables();
}

/**
 * Sets the application's CSS variables to a high contrast color scheme.
 *
 * This method updates the CSS variables to a high contrast color scheme, enhancing visibility
 * and readability. It can be called when user enables high contrast mode, typically useful
 * for visually impaired users or users who prefer high contrast settings.
 *
 * @returns {undefined} Does not return a value directly but causes side effects by
 * modifying global css variables.
 */
export function setContrastHigh() {
  const variables = {
    '--white': '#FFFFFF',
    '--black': '#000000',
    '--lblue': '#00B4E9',
    '--blue': '#20499F',
    '--blue-text': '#20499F',
    '--red': '#F03A47',
    '--yellow': '#FFCB1A',
    '--lgray': '#F5F5F5',
    '--gray': '#979797',
    '--dgray': '#4A4A4A',
    '--review-gray': '#D8D8D8',
    '--chevron-invert': '100%',
    '--icon-invert': '0%',
  };

  cssVariablesState = { ...cssVariablesState, ...variables };

  cssVars({
    preserveVars: false,
    variables: cssVariablesState,
  });
}

/**
 * Updates the CSS variables to low contrast scheme.
 *
 * This function is primarily used in creating a low contrast mode for the user interface.
 * It modifies CSS variables and decks out the interface in colors more suitable for
 * human vision. Once the function is called, the global `cssVariablesState` is updated
 * with the new color scheme dictionary.
 *
 * Uses `cssVars` function to directly update the CSS variables on the page. Prior to
 * updating the page's CSS variables, it merges the existing state with an object of new
 * color values appropriate for low contrast. The `preserveVars` field is set to false
 * which signals to overwrite any existing CSS variables.
 */
export function setContrastLow() {
  const variables = {
    '--white': '#000000',
    '--black': '#FFFFFF',
    '--lblue': '#20499F',
    '--blue': '#00B4E9',
    '--blue-text': '#FFFFFF',
    '--red': '#F03A47',
    '--yellow': '#4A4A4A',
    '--lgray': '#979797',
    '--gray': '#F5F5F5',
    '--dgray': '#FFCB1A',
    '--review-gray': '#979797',
    '--chevron-invert': '0%',
    '--icon-invert': '100%',
  };

  cssVariablesState = { ...cssVariablesState, ...variables };

  cssVars({
    preserveVars: false,
    variables: cssVariablesState,
  });
}

/**
 * Sets CSS variables for a given language direction.
 *
 * The function changes certain CSS variables depending on the language direction (ltr or rtl).
 * It uses cssVars() function to set these variables which will then be used by the CSS.
 * These direction-specific variables are used to correctly display UI elements in different
 * languages that have different reading directions. For example, readings in English are
 * from left to right (ltr), while in Farsi are from right to left (rtl). The function also
 * accommodates for different word breaks in the Korean language.
 *
 * The function also additional positioning elements for the "FA" language for fixed items
 * within grid layout due to the fact that the rtl fix using scaleX(-1) method does not
 * adequately accommodate this.
 *
 * @param {string} language - Passed language as a string. Common values include: 'FA', 'KO'.
 */
export function setLanguageCSS(language) {
  const isRTL = language === 'FA' || language === 'AR';
  const variables = {
    '--scale-x': isRTL ? 'scaleX(-1)' : 'scaleX(1)',
    '--text-direction': isRTL ? 'rtl' : 'ltr',
    '--text-align': isRTL ? 'right' : 'left',
    '--word-break': language === 'KO' ? 'keep-all' : 'normal',
    /**
     * Since the rtl fix using scaleX(-1) fix does not accomodate for fixed items within
     * with grid layout, additional variables below are used to handle positioning of elements
     */
    '--contest-list-wrapper-right': isRTL ? contestListWrapperPosition : 'initial',
    '--contest-list-wrapper-left': isRTL ? 'initial' : contestListWrapperPosition,
    '--content-wrapper-margin-right': isRTL ? contentWrapperMargin : 'initial',
    '--content-wrapper-margin-left': isRTL ? 'initial' : contentWrapperMargin,
    '--text-direction-reverse': isRTL ? 'ltr' : 'rtl',
  };

  cssVariablesState = { ...cssVariablesState, ...variables };

  cssVars({
    preserveVars: false,
    variables: cssVariablesState,
  });
}

/**
 * Updates CSS variables to smaller text sizes and related display sizes.
 *
 * This function defines a mapping of CSS variable names to new sizes, effectively
 * setting the new sizes all at once. It specifically caters to scenarios where a smaller
 * text size is preferable for better readability or space utilitization. It updates sizes
 * related to fonts, buttons, heights, and widths among others. The new sizes are then
 * distributed to the entire application since they affect the global CSS state.
 */
export function setTextSizeSmall() {
  const variables = {
    '--font-size-base': '8.6px',
    '--font-small': '1.2rem',
    '--font-small-10': '1.25rem',
    '--font-small-20': '1.3rem',
    '--font-medium': '1.4rem',
    '--font-medium-10': '1.5rem',
    '--font-medium-20': '1.6rem',
    '--font-large': '1.8rem',
    '--font-large-20': '2.8rem',
    '--font-xlarge': '3.6rem',
    '--font-xxlarge': '4.4rem',
    '--med-btn-width': '180px',
    '--lg-btn-width': '243px',
    '--xlg-btn-width': '347px',
    '--wayfinding-step-height': '187px',
    '--privacy-choice-height': '400px',
    '--candidate-height': '80px',
    '--sample-text-height': '130px',
    '--review-modal-max-width': '540px',
    '--header-spacer-12-4': '12px',
  };

  cssVariablesState = { ...cssVariablesState, ...variables };

  cssVars({
    preserveVars: false,
    variables: cssVariablesState,
  });
}

/**
 * Sets the global CSS variables for medium text size.
 *
 * This function modifies the global CSS variables by spreading the "variables" object
 * into the "cssVariablesState" object. Then, it sets these new variables into the
 * document's styles using the cssVars function. The parameters or values correspond to
 * specific font sizes and element dimensions of the interface.
 *
 * No parameters are required to run this function. It's designed to execute without
 * user input data, operating solely with internal data in order to update the global
 * CSS variables. The changes will be reflected in all elements that use these specific
 * variables in their styles.
 *
 * @returns {undefined} This function does not have a return value. Its sole purpose is
 * to perform a side effect by updating the global CSS variables.
 */
export function setTextSizeMedium() {
  const variables = {
    '--font-size-base': '10px',
    '--font-small': '1.4rem',
    '--font-small-10': '1.6rem',
    '--font-small-20': '1.8rem',
    '--font-medium': '2.0rem',
    '--font-medium-10': '2.4rem',
    '--font-medium-20': '2.8rem',
    '--font-large': '3.2rem',
    '--font-large-20': '3.8rem',
    '--font-xlarge': '4.0rem',
    '--font-xxlarge': '4.8rem',
    '--med-btn-width': '180px',
    '--lg-btn-width': '243px',
    '--xlg-btn-width': '347px',
    '--wayfinding-step-height': '187px',
    '--privacy-choice-height': '420px',
    '--candidate-height': '80px',
    '--sample-text-height': '130px',
    '--review-modal-max-width': '540px',
    '--header-spacer-12-4': '12px',

    /* 320px Special Attention */
    '--iphone5-header-default-button-width': '68px',
    '--iphone5-header-default-button-right': '14px',
    '--iphone5-header-review-button-width': '74px',
    '--iphone5-header-review-button-right': '4px',
    '--iphone5-header-crest-left': '0px',
  };

  cssVariablesState = { ...cssVariablesState, ...variables };

  cssVars({
    preserveVars: false,
    variables: cssVariablesState,
  });
}

/**
 * Adjusts the CSS variables for a larger text size across the application.
 *
 * This function modifies the base font sizes and dimensions of some specific components to
 * accommodate a larger text size better. The CSS variables are modified in the cssVariablesState,
 * which are then applied to the application CSS through the cssVars function. Preserving previous
 * CSS variables is not necessary as we are setting new values for each property.
 *
 * This function can be used when the user decides to choose a larger text size for better
 * accessibility or readability on the interface.
 */
export function setTextSizeLarge() {
  const variables = {
    '--font-size-base': '11.4px',
    '--font-small': '1.8rem',
    '--font-small-10': '1.9rem',
    '--font-small-20': '2.0rem',
    '--font-medium': '2.2rem',
    '--font-medium-10': '2.6rem',
    '--font-medium-20': '3.0rem',
    '--font-large': '3.4rem',
    '--font-large-20': '3.8rem',
    '--font-xlarge': '4.8rem',
    '--font-xxlarge': '5.6rem',
    '--med-btn-width': '200px',
    '--lg-btn-width': '270px',
    '--xlg-btn-width': '347px',
    '--wayfinding-step-height': '210px',
    '--privacy-choice-height': '450px',
    '--candidate-height': '100px',
    '--sample-text-height': '180px',
    '--review-modal-max-width': '620px',
    '--header-spacer-12-4': '4px',

    /* 320px Special Attention */
    '--iphone5-header-default-button-width': '74px',
    '--iphone5-header-default-button-right': '4px',
    '--iphone5-header-review-button-width': '74px',
    '--iphone5-header-review-button-right': '4px',
    '--iphone5-header-crest-left': '10px',
  };

  cssVariablesState = { ...cssVariablesState, ...variables };

  cssVars({
    preserveVars: false,
    variables: cssVariablesState,
  });
}
/**
 * This function constructs a string of CSS class names based on the properties of an object.
 *
 * It accepts an object where each key represents a CSS class name and the corresponding value
 * dictates whether that class should be included in the output string. This manipulation allows
 * for dynamic className setting based on conditional logic. Any class name with an 'undefined'
 * string or a falsy value won't be included in the final string.
 *
 * @param {object} classes - An object containing CSS class names as keys and boolean values for inclusion.
 * @returns {string} A string of CSS class names built from the keys of the 'classes' object
 * where their corresponding values are truthy.
 */
export function classNames(classes) {
  return (
    Object.entries(classes)
      // className of string 'undefined' means the css module doesn't have that class
      .filter(([className, enabled]) => Boolean(className !== 'undefined' && enabled))
      .map(([className]) => className)
      .join(' ')
  );
}
